import { ZfToastProvider, ZfTypography } from '@zafire/react';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { TanStackQueryProvider } from './api';
import { Authenticated, ConfiguredAuthProvider } from './components/auth';
import { HashRoutingProvider } from './components/routing';
import { ErrorScreen } from './container';
import { AppEnvironmentProvider } from './contexts/app-environment-context';
import { LayoutContext } from './contexts/layout-context';
import { YwuAppProvider } from './contexts/ywu-app-context';
import { BannerProvider } from './error/BannerProvider';
import type { IWealthNavigatorMicroFrontendProps } from './interfaces/wealth-navigator';

interface IAppWrapperProps extends PropsWithChildren, IWealthNavigatorMicroFrontendProps {
    totalNavbarHeight: number;
    bodyElement?: HTMLElement;
    shadowRoot?: ShadowRoot;
    auth: { silentRedirectPath: string };
    silentLogin?: boolean;
}

export const AppWrapper: FunctionComponent<IAppWrapperProps> = ({
    totalNavbarHeight,
    bodyElement = document.body,
    shadowRoot,
    auth,
    silentLogin = true,
    isWealthNavigatorMFE = false,
    dispatchPresentationModeEvent,
    dispatchReturnToSessionsEvent,
    dispatchNavigateToClientContextEvent,
    children,
}) => (
    <LayoutContext.Provider value={{ totalNavbarHeight, bodyElement, shadowRoot }}>
        <TanStackQueryProvider>
            <HashRoutingProvider>
                <ZfTypography>
                    <YwuAppProvider
                        isWealthNavigatorMFE={isWealthNavigatorMFE}
                        dispatchPresentationModeEvent={dispatchPresentationModeEvent}
                        dispatchReturnToSessionsEvent={dispatchReturnToSessionsEvent}
                        dispatchNavigateToClientContextEvent={dispatchNavigateToClientContextEvent}
                    >
                        <ErrorBoundary FallbackComponent={() => <ErrorScreen />}>
                            <ConfiguredAuthProvider silentRedirectPath={auth.silentRedirectPath}>
                                <Authenticated silent={silentLogin}>
                                    <BannerProvider>
                                        <AppEnvironmentProvider>{children}</AppEnvironmentProvider>
                                    </BannerProvider>
                                </Authenticated>
                            </ConfiguredAuthProvider>
                        </ErrorBoundary>
                    </YwuAppProvider>
                </ZfTypography>
            </HashRoutingProvider>
            {!isWealthNavigatorMFE && <ZfToastProvider />}
        </TanStackQueryProvider>
    </LayoutContext.Provider>
);
